import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Building2, Eye, Ban, Power, KeyRound, Trash2, Download, Receipt, Activity } from "lucide-react";

const TABS = [
  { value: "all", label: "All" },
  { value: "PENDING_APPROVAL", label: "Pending" },
  { value: "APPROVED", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "REJECTED", label: "Expired/Rejected" },
];

function statusBadge(s) {
  const tone = { APPROVED: "bg-emerald-100 text-emerald-900", PENDING_APPROVAL: "bg-amber-100 text-amber-900",
                  SUSPENDED: "bg-orange-100 text-orange-900", REJECTED: "bg-red-100 text-red-900" }[s] || "";
  return <Badge variant="outline" className={`${tone} font-mono text-[10px]`}>{s}</Badge>;
}

/**
 * Reusable tenants admin page — one component, three portals (ORGANIZATION / VENDOR / DISTRIBUTOR).
 *
 * Props:
 *   portal:       "ORGANIZATION" | "VENDOR" | "DISTRIBUTOR"
 *   title:        Page heading (e.g. "Organizations", "Vendors")
 *   subtitle:     Optional descriptive paragraph
 *   entityLabel:  Singular label shown in messages/columns (e.g. "Organization", "Vendor")
 *   Icon:         lucide icon component
 *   testId:       root data-testid (e.g. "admin-organizations")
 *   rowTestPrefix: row testid prefix (e.g. "org", "vendor", "dist")
 */
export default function AdminTenantsPage({
  portal,
  title,
  subtitle,
  entityLabel = "Organization",
  Icon = Building2,
  testId = "admin-organizations",
  rowTestPrefix = "org",
}) {
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [planOpen, setPlanOpen] = useState(false);
  const [planForm, setPlanForm] = useState({ tenant_id: "", plan_code: "", billing_cycle: "MONTHLY" });
  const [plans, setPlans] = useState([]);
  const [trialCount, setTrialCount] = useState(0);

  const load = async () => {
    const params = { portal };
    if (tab !== "all") params.status_filter = tab;
    const { data } = await api.get("/admin/tenants", { params });
    setItems(data);
    const allRes = await api.get("/admin/tenants", { params: { portal } });
    setTrialCount(allRes.data.filter((t) => t.subscription?.status === "TRIALING").length);
  };
  const loadPlans = async () => {
    const { data } = await api.get("/admin/plans");
    setPlans(data);
  };
  useEffect(() => { load(); loadPlans(); }, [tab, portal]);

  const act = async (id, action) => {
    try { await api.post(`/admin/tenants/${id}/action`, { action }); toast.success(`${entityLabel} ${action.toLowerCase()}d`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm(`Soft-delete this ${entityLabel.toLowerCase()}? Users will be suspended.`)) return;
    try { await api.delete(`/admin/tenants/${id}`); toast.success(`${entityLabel} deleted`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const exportData = async (id, name) => {
    try {
      const { data } = await api.get(`/admin/tenants/${id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${name}-export.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const impersonate = async (id) => {
    try {
      const { data } = await api.post(`/admin/tenants/${id}/impersonate`);
      toast.success(`Impersonation token issued for ${data.as_user} (${data.expires_in_minutes}m).`);
      navigator.clipboard.writeText(data.token).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openPlan = (t) => { setPlanForm({ tenant_id: t.id, plan_code: plans[0]?.code || "", billing_cycle: "MONTHLY" }); setPlanOpen(true); };
  const assignPlan = async () => {
    try {
      await api.post(`/admin/tenants/${planForm.tenant_id}/subscription`, { plan_code: planForm.plan_code, billing_cycle: planForm.billing_cycle });
      toast.success("Subscription assigned");
      setPlanOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const visible = items.filter((i) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (i.name || "").toLowerCase().includes(f) || (i.slug || "").toLowerCase().includes(f);
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">{title}</h1>
          {subtitle ? <p className="text-muted-foreground">{subtitle}</p> : null}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {TABS.map((t) => <TabsTrigger key={t.value} value={t.value} data-testid={`${rowTestPrefix}-tab-${t.value}`}>{t.label}</TabsTrigger>)}
            <TabsTrigger value="trial" data-testid={`${rowTestPrefix}-tab-trial`}>Trial · {trialCount}</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input placeholder="Search by name or slug…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-xs" data-testid={`${rowTestPrefix}-search`} />
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{entityLabel}</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Users</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible
              .filter((t) => tab !== "trial" || t.subscription?.status === "TRIALING")
              .map((t) => (
              <TableRow key={t.id} data-testid={`${rowTestPrefix}-row-${t.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                    <span className="font-semibold">{t.name}</span>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">{t.slug}</TableCell>
                <TableCell>{t.user_count}</TableCell>
                <TableCell className="text-sm">{t.subscription?.plan_name || <span className="text-muted-foreground">No plan</span>}<br /><span className="font-mono text-[10px] text-muted-foreground">{t.subscription?.status || "—"}</span></TableCell>
                <TableCell>{statusBadge(t.status)}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Link to={`/admin/tenants/${t.id}/activity`}>
                    <Button size="sm" variant="ghost" data-testid={`activity-${t.id}`} title="View activity & portal URL">
                      <Activity className="w-4 h-4 text-emerald-600" />
                    </Button>
                  </Link>
                  {t.status === "PENDING_APPROVAL" && <Button size="sm" onClick={() => act(t.id, "APPROVE")} data-testid={`approve-${t.id}`}>Approve</Button>}
                  {t.status === "APPROVED" && <Button size="sm" variant="outline" onClick={() => act(t.id, "SUSPEND")} data-testid={`suspend-${t.id}`} title="Suspend"><Ban className="w-4 h-4" /></Button>}
                  {t.status === "SUSPENDED" && <Button size="sm" onClick={() => act(t.id, "ACTIVATE")} data-testid={`activate-${t.id}`} title="Reactivate"><Power className="w-4 h-4" /></Button>}
                  <Button size="sm" variant="ghost" onClick={() => openPlan(t)} data-testid={`plan-${t.id}`} title="Assign plan"><Receipt className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => impersonate(t.id)} data-testid={`impersonate-${t.id}`} title={`Impersonate ${entityLabel} Admin`}><KeyRound className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => exportData(t.id, t.slug)} data-testid={`export-${t.id}`} title="Export"><Download className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(t.id)} data-testid={`delete-${t.id}`} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => window.location.assign(`/t/${t.slug}`)} title="View portal"><Eye className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No {entityLabel.toLowerCase()}s.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign subscription plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Plan</label>
              <Select value={planForm.plan_code} onValueChange={(v) => setPlanForm({ ...planForm, plan_code: v })}>
                <SelectTrigger data-testid="plan-select"><SelectValue placeholder="Pick a plan" /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => <SelectItem key={p.id} value={p.code}>{p.name} · ₹{p.monthly_price}/mo</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Billing cycle</label>
              <Select value={planForm.billing_cycle} onValueChange={(v) => setPlanForm({ ...planForm, billing_cycle: v })}>
                <SelectTrigger data-testid="plan-cycle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TRIAL">Trial</SelectItem>
                  <SelectItem value="MONTHLY">Monthly</SelectItem>
                  <SelectItem value="YEARLY">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanOpen(false)}>Cancel</Button>
            <Button onClick={assignPlan} data-testid="plan-confirm">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
